/**
 * 권한 이름 — 목록(EA-35)·만들기/고치기(EA-36)·지우기(EA-37). 레거시 pending 「관리자 관리」
 * (`/admins`) 자리에 새로 만든 화면이다(경로 `/permission-names` — 제안 03 1번).
 *
 * 계약: XM-18(고를 수 있는 페이지·버튼)·XM-19(목록)·XM-20(만들기)·XM-21(고치기 — keys 전체를
 * 덮어씀)·XM-22(지우기 — 쓰는 직원이 없을 때만). 모두 **최고관리자만**.
 *
 * 결정: 사장님 「실상 직원에게 관리자라는 권한을지정만하고 내부적으로 관리자는 어떤페이지
 * 어떤버튼 설정하는거지 이해되나?」 — 고치면 그 이름을 가진 직원 모두에게 바로 적용된다.
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createPermissionName, deletePermissionName, listPermissionNames, listPermissionPages,
  updatePermissionName,
} from '@/api/admin'
import { setFlash } from '@/auth/pendingWrite'
import { AdminRef, Badge, Button, Card, Code, Field, Labeled, Select, TD, TH, Time } from '@/components/ui'
import { Confirm, submitWrite, useWrite } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import {
  AUDIT_SCOPE_LABEL, type AuditScope, type PermissionNameRow, type PermissionPage,
} from '@/api/types'

/**
 * 이 웹에 화면이 없는 키(명세 1-3 '비워 둠' — 지갑 조회·정산·에스크로 설정). 페이지의 버튼이
 * 모두 이것이면 그 페이지 옆에 「화면 없음」(제안 03 4번).
 *
 * 발견-401(2026-10-14): 보관 파일(`archives.view`)·보존·파기(`retention.view`·`retention.edit`·
 * `retention.decide`)는 2026-09-30 에 화면을 만들었다(메뉴 「Retention & archives」 `/records`,
 * 03 5번 「비워 둠에서 뺀다」) — 그 키를 주면 메뉴가 보이므로 「화면 없음」을 붙이지 않는다.
 */
const NO_SCREEN_KEYS: ReadonlySet<string> = new Set([
  'wallets.view', 'wallets.risk.view', 'settlements.view',
  'escrow_setup.view', 'escrow_setup.run',
])

function noScreen(page: PermissionPage): boolean {
  return page.buttons.length > 0 && page.buttons.every((b) => NO_SCREEN_KEYS.has(b.key))
}

/* ── EA-35 목록 (XM-19) ──────────────────────────────────────────── */

export function PermissionNamesPage() {
  const q = useQuery({ queryKey: ['permission-names'], queryFn: listPermissionNames })
  const [editing, setEditing] = useState<PermissionNameRow | 'new' | null>(null)
  const [deleting, setDeleting] = useState<PermissionNameRow | null>(null)
  const rows = q.data?.names ?? []

  return (
    <Card title="Permission names">
      <p className="mb-4 text-xs text-slate-500">
        Sets the pages and buttons each permission name may use. Each staff member gets only one permission
        name. Changes apply right away to all staff members with that name.
      </p>
      <div className="mb-4">
        <Button onClick={() => setEditing('new')}>Create permission name</Button>
      </div>
      <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
        emptyText="There are no permission names.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Name</th><th className={TH}>Pages and buttons</th>
            <th className={TH}>Audit log view scope</th><th className={TH}>Staff</th>
            <th className={TH}>Changed at</th><th className={TH}>Changed by</th><th className={TH}>Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((n) => (
              <tr key={n.id}>
                <td className={TD}>{n.name}</td>
                <td className={TD}>{n.keys.length}</td>
                <td className={TD}><Labeled labels={AUDIT_SCOPE_LABEL} code={n.auditScope} /></td>
                <td className={TD}>{n.staffCount}</td>
                <td className={TD}><Time ms={n.updatedAt} /></td>
                <td className={TD}><AdminRef adminId={n.updatedByAdminId} /></td>
                <td className={TD}>
                  <div className="flex gap-2">
                    <Button tone="plain" onClick={() => setEditing(n)}>Edit</Button>
                    {/* ⚠ 쓰는 직원이 있으면 못 지운다(서버도 409 `name_in_use`). */}
                    <Button tone="plain" disabled={n.staffCount !== 0} onClick={() => setDeleting(n)}>
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>

      {editing !== null && (
        <EditDialog row={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
      {deleting !== null && <DeleteDialog row={deleting} onClose={() => setDeleting(null)} />}
    </Card>
  )
}

/* ── EA-36 만들기·고치기 (XM-20 · XM-21) ─────────────────────────── */

function EditDialog({ row, onClose }: {
  row: PermissionNameRow | null
  onClose: () => void
}) {
  const qc = useQueryClient()
  const pages = useQuery({ queryKey: ['permission-pages'], queryFn: listPermissionPages })
  const w = useWrite()
  const [name, setName] = useState(row?.name ?? '')
  const [keys, setKeys] = useState<Set<string>>(() => new Set(row?.keys ?? []))
  const [scope, setScope] = useState<AuditScope>((row?.auditScope as AuditScope | undefined) ?? 'OWN_OPERATIONS')

  function toggle(key: string, on: boolean) {
    setKeys((prev) => {
      const next = new Set(prev)
      if (on) next.add(key)
      else next.delete(key)
      return next
    })
  }

  async function submit() {
    const input = { name: name.trim(), keys: [...keys].sort(), auditScope: scope }
    const send = async () => {
      if (row) await updatePermissionName(row.id, input)
      else await createPermissionName(input)
      setFlash({
        tone: 'ok',
        text: row ? `Updated the permission name "${input.name}".` : `Created the permission name "${input.name}".`,
      })
      await qc.invalidateQueries({ queryKey: ['permission-names'] })
    }
    await submitWrite(w, row ? 'Edit permission name' : 'Create permission name', send, onClose)
  }

  return (
    <Confirm open wide title={row ? `Edit permission name — ${row.name}` : 'Create permission name'} action="Save"
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={name.trim() === ''}>
      <div className="flex flex-col gap-3">
        <Field label="Name" value={name} maxLength={64} onChange={(e) => setName(e.target.value)} />
        <Select label="Audit log view scope" value={scope} onChange={(v) => setScope(v as AuditScope)}
          options={(Object.keys(AUDIT_SCOPE_LABEL) as AuditScope[]).map((s) => ({ value: s, label: AUDIT_SCOPE_LABEL[s] }))} />

        <div>
          <p className="mb-1 font-medium">Pages and buttons</p>
          <QueryState isPending={pages.isPending} error={pages.error}>
            <div className="flex flex-col gap-3">
              {(pages.data?.pages ?? []).map((p) => (
                <fieldset key={p.page} className="rounded border border-slate-200 p-3 dark:border-slate-700">
                  <legend className="px-1 text-sm font-semibold">
                    {p.label}
                    {noScreen(p) && <Badge>No screen</Badge>}
                  </legend>
                  <div className="flex flex-col gap-1">
                    {p.buttons.map((b) => (
                      <label key={b.key} className="flex items-center gap-2">
                        <input type="checkbox" checked={keys.has(b.key)}
                          onChange={(e) => toggle(b.key, e.target.checked)} />
                        <span>{b.label} <Code>{b.key}</Code></span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
          </QueryState>
        </div>

        {row && (
          <p className="text-xs text-slate-500">
            Saving applies right away to the {row.staffCount} staff members with this name.
          </p>
        )}
      </div>
    </Confirm>
  )
}

/* ── EA-37 지우기 (XM-22) ────────────────────────────────────────── */

function DeleteDialog({ row, onClose }: { row: PermissionNameRow; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()

  async function submit() {
    const send = async () => {
      await deletePermissionName(row.id)
      setFlash({ tone: 'ok', text: `Deleted the permission name "${row.name}".` })
      await qc.invalidateQueries({ queryKey: ['permission-names'] })
    }
    await submitWrite(w, 'Delete permission name', send, onClose)
  }

  return (
    <Confirm open title="Delete permission name" action="Delete" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}>
      <p>Deletes &quot;{row.name}&quot;.</p>
    </Confirm>
  )
}
